// Documentos e dados do solicitante do cancelamento (#5828, projeto 11).
//
// Regras puras: o que é um arquivo aceitável, quem pode estar pedindo e quando
// falta documento. O acesso ao bucket privado `cancelamento-docs` fica no fim
// do arquivo (import dinâmico), como no resto de src/lib.
//
// Decisões de 24/09/2026: até 10 arquivos por pedido, 20 MB cada, em PDF, JPG,
// PNG ou HEIC; pedido novo exige ao menos 1 documento; só adm e gerente apagam.

export const LIMITE_ARQUIVOS_POR_PEDIDO = 10;
export const TAMANHO_MAXIMO_BYTES = 20 * 1024 * 1024;

/** Tipos aceitos. HEIC entra porque é o padrão da foto de iPhone. */
export const TIPOS_ACEITOS = [
  'application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/heif',
] as const;

const EXTENSAO_POR_TIPO: Record<string, string> = {
  'application/pdf': 'pdf', 'image/jpeg': 'jpg', 'image/png': 'png',
  'image/heic': 'heic', 'image/heif': 'heif',
};

export type VinculoSolicitante = 'titular' | 'representante_legal' | 'procurador' | 'outro';

export const VINCULOS_SOLICITANTE: Array<{ valor: VinculoSolicitante; label: string; pedeDocumento: string }> = [
  { valor: 'titular', label: 'Sou o titular da conta', pedeDocumento: 'um documento com foto do titular' },
  { valor: 'representante_legal', label: 'Represento a empresa titular', pedeDocumento: 'o contrato social ou uma procuração' },
  { valor: 'procurador', label: 'Sou procurador do titular', pedeDocumento: 'a procuração e um documento com foto' },
  { valor: 'outro', label: 'Outro vínculo', pedeDocumento: 'um documento que comprove o vínculo' },
];

export interface ArquivoParaEnvio {
  name: string;
  size: number;
  type: string;
}

/**
 * Motivo de recusa do arquivo, ou null quando ele pode subir. Texto escrito para
 * o cliente ler no Portal — nada de "MIME type inválido".
 */
export function motivoArquivoRecusado(
  arquivo: ArquivoParaEnvio | null | undefined,
  jaEnviados = 0,
): string | null {
  if (!arquivo || !String(arquivo.name ?? '').trim()) return 'Escolha um arquivo para enviar.';
  if (jaEnviados >= LIMITE_ARQUIVOS_POR_PEDIDO) {
    return `Você já enviou ${LIMITE_ARQUIVOS_POR_PEDIDO} arquivos neste pedido.`;
  }
  const tamanho = Number(arquivo.size);
  if (!Number.isFinite(tamanho) || tamanho <= 0) return 'O arquivo parece estar vazio. Tente outro.';
  if (tamanho > TAMANHO_MAXIMO_BYTES) {
    return `O arquivo tem mais de ${Math.round(TAMANHO_MAXIMO_BYTES / 1024 / 1024)} MB. Envie uma versão menor.`;
  }
  if (!(TIPOS_ACEITOS as readonly string[]).includes(String(arquivo.type ?? '').toLowerCase())) {
    return 'Formato não aceito. Envie PDF, JPG, PNG ou foto do celular.';
  }
  return null;
}

/** Só letras, números, ponto, hífen e sublinhado: nome de arquivo vira caminho no bucket. */
export function nomeSeguroDeArquivo(nome: string, tipo?: string): string {
  const original = String(nome ?? '').trim();
  const semAcento = original.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const limpo = semAcento.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/-+/g, '-').replace(/^[-.]+/, '');
  const base = limpo.slice(-80) || `documento.${EXTENSAO_POR_TIPO[String(tipo ?? '').toLowerCase()] ?? 'dat'}`;
  return /\.[A-Za-z0-9]{2,5}$/.test(base)
    ? base
    : `${base}.${EXTENSAO_POR_TIPO[String(tipo ?? '').toLowerCase()] ?? 'dat'}`;
}

/**
 * Caminho no bucket: <solicitacao>/<uid de quem envia>/<carimbo>-<arquivo>.
 * A pasta do meio é o que a policy do bucket usa para o cliente só escrever na
 * própria pasta — mudar isso aqui quebra a regra do banco.
 */
export function caminhoDocumento(
  solicitacaoId: number,
  usuarioId: string,
  arquivo: ArquivoParaEnvio,
  agora: Date,
): string {
  return `${solicitacaoId}/${usuarioId}/${agora.getTime()}-${nomeSeguroDeArquivo(arquivo.name, arquivo.type)}`;
}

export interface DocumentoDoPedido {
  id: number;
  nome_original: string;
  tipo_documento?: string | null;
}

/**
 * O que falta anexar. Enquanto a demanda 01 não define a lista por perfil,
 * a exigência é "ao menos um documento" (decisão de 24/09/2026) — por isso
 * `obrigatorios` é opcional e já deixa a regra pronta para quando existir.
 */
export function documentosQueFaltam(
  documentos: DocumentoDoPedido[] | null | undefined,
  obrigatorios?: string[] | null,
): string[] {
  const enviados = documentos || [];
  const lista = (obrigatorios || []).map(t => String(t ?? '').trim()).filter(Boolean);
  if (lista.length === 0) {
    return enviados.length === 0 ? ['ao menos um documento que comprove o pedido'] : [];
  }
  const tipos = new Set(enviados.map(d => String(d.tipo_documento ?? '').trim()).filter(Boolean));
  return lista.filter(t => !tipos.has(t));
}

/** Quem está pedindo, para a tela e para a mensagem. */
export function descreverSolicitante(
  nome: string | null | undefined,
  vinculo: string | null | undefined,
): string | null {
  const n = String(nome ?? '').trim();
  if (!n) return null;
  const meta = VINCULOS_SOLICITANTE.find(v => v.valor === vinculo);
  return meta && meta.valor !== 'titular' ? `${n} (${meta.label.toLowerCase()})` : n;
}

// ===========================================================================
// Acesso ao bucket privado. Import dinâmico do supabase para o bloco puro acima
// continuar rodando no `node --test`.
// ===========================================================================

export const BUCKET_DOCUMENTOS = 'cancelamento-docs';

export async function enviarDocumentoCancelamento(
  solicitacaoId: number,
  usuarioId: string,
  arquivo: File,
  opcoes: { peloPortal: boolean; tipoDocumento?: string | null; jaEnviados?: number },
): Promise<string> {
  const recusa = motivoArquivoRecusado(arquivo, opcoes.jaEnviados ?? 0);
  if (recusa) throw new Error(recusa);

  const { supabase } = await import('./supabase');
  const caminho = caminhoDocumento(solicitacaoId, usuarioId, arquivo, new Date());
  const { error } = await supabase.storage.from(BUCKET_DOCUMENTOS).upload(caminho, arquivo, { upsert: false });
  if (error) throw new Error(`Não foi possível enviar o arquivo: ${error.message}`);

  if (opcoes.peloPortal) {
    // O consumidor não escreve na tabela: quem registra é a função do servidor.
    const { error: erroRegistro } = await supabase.rpc('registrar_documento_cancelamento_portal', {
      p_solicitacao_id: solicitacaoId,
      p_caminho: caminho,
      p_nome_original: arquivo.name,
      p_tipo_mime: arquivo.type || null,
      p_tamanho_bytes: arquivo.size ?? null,
      p_tipo_documento: opcoes.tipoDocumento ?? null,
    });
    if (erroRegistro) {
      // Sem registro, o arquivo vira lixo invisível no bucket.
      await supabase.storage.from(BUCKET_DOCUMENTOS).remove([caminho]).catch(() => {});
      throw new Error(erroRegistro.message);
    }
  } else {
    const { error: erroRegistro } = await supabase.from('cancelamento_documentos').insert({
      solicitacao_id: solicitacaoId,
      caminho,
      nome_original: arquivo.name,
      tipo_mime: arquivo.type || null,
      tamanho_bytes: arquivo.size ?? null,
      tipo_documento: opcoes.tipoDocumento ?? null,
      enviado_por: usuarioId,
      enviado_no_portal: false,
    });
    if (erroRegistro) {
      await supabase.storage.from(BUCKET_DOCUMENTOS).remove([caminho]).catch(() => {});
      throw erroRegistro;
    }
  }
  return caminho;
}

/** Link temporário (10 minutos) para abrir o documento. */
export async function linkDocumentoCancelamento(caminho: string): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase.storage.from(BUCKET_DOCUMENTOS).createSignedUrl(caminho, 600);
  if (error || !data?.signedUrl) throw new Error('Não foi possível abrir o documento agora.');
  return data.signedUrl;
}

export async function listarDocumentosDaSolicitacao(solicitacaoId: number) {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase
    .from('cancelamento_documentos')
    .select('id, caminho, nome_original, tipo_mime, tamanho_bytes, tipo_documento, enviado_no_portal, created_at')
    .eq('solicitacao_id', solicitacaoId)
    .order('created_at');
  if (error) throw error;
  return data || [];
}

/** Documentos do próprio cliente, pelo Portal (função SECURITY DEFINER). */
export async function meusDocumentosDoPedido(solicitacaoId: number) {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase.rpc('meus_documentos_cancelamento_portal', {
    p_solicitacao_id: solicitacaoId,
  });
  if (error) throw error;
  return (data || []) as Array<{
    id: number; caminho: string; nome_original: string;
    tipo_mime: string | null; tamanho_bytes: number | null; created_at: string;
  }>;
}

/** Remoção (só adm e gerente pela RLS): tira o registro e o arquivo. */
export async function apagarDocumentoCancelamento(id: number, caminho: string): Promise<void> {
  const { supabase } = await import('./supabase');
  const { error } = await supabase.from('cancelamento_documentos').delete().eq('id', id);
  if (error) throw error;
  const { error: erroArquivo } = await supabase.storage.from(BUCKET_DOCUMENTOS).remove([caminho]);
  if (erroArquivo) console.warn('Documento removido do registro, mas o arquivo continuou no bucket:', erroArquivo.message);
}

/**
 * Tamanho de arquivo legível. Em MB fixo, todo documento pequeno virava
 * "0.0 MB" na tela — o analista não sabia se o cliente mandou um arquivo
 * minúsculo ou se o upload falhou. A unidade acompanha o tamanho.
 */
export function formatarTamanhoArquivo(bytes: number | null | undefined): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${Math.round(n)} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  const mb = n / 1024 / 1024;
  // Até 10 MB uma casa ajuda; acima disso o inteiro basta.
  return mb < 10 ? `${mb.toFixed(1)} MB` : `${Math.round(mb)} MB`;
}

// ===========================================================================
// Documentos obrigatórios por vínculo (#5828, item da #5825)
// ===========================================================================
//
// ⚠️ Lista PROVISÓRIA. A definitiva é decisão de negócio (demanda 01, item 5) e
// ainda não veio. Esta aqui é a proposta da TI, montada a partir do que o
// contrato e a prática do atendimento já exigem — serve para o mecanismo
// existir e para o Vinicius Alves ter algo concreto para aprovar ou corrigir,
// em vez de uma pergunta em aberto.
//
// Enquanto `cancelamento_documentos_modo` for 'aviso', falta de documento
// aparece como ATENÇÃO na validação e não impede nada. Vira bloqueio no dia em
// que a lista for aprovada e alguém trocar a configuração — sem deploy.

export const DOCUMENTOS_LISTA_PROVISORIA = true;

export type ModoDocumentos = 'aviso' | 'bloqueio';

export const DOCUMENTOS_OBRIGATORIOS_PADRAO: Record<string, string[]> = {
  titular: ['documento com foto do titular'],
  representante_legal: ['contrato social', 'cartão CNPJ', 'documento com foto do representante'],
  procurador: ['procuração', 'documento com foto do procurador'],
  outro: ['documento com foto de quem está pedindo', 'comprovante do vínculo com a unidade'],
};

/**
 * O que este perfil precisa anexar. Vínculo desconhecido ou em branco cai no
 * 'titular': é o caso mais comum e o menos exigente — inventar exigência para
 * um vínculo que ninguém cadastrou trava pedido legítimo.
 */
export function documentosObrigatoriosDoVinculo(
  vinculo: string | null | undefined,
  configuracao?: Record<string, string[]> | null,
): string[] {
  const chave = String(vinculo ?? '').trim() || 'titular';
  const cfg = configuracao && Object.keys(configuracao).length > 0 ? configuracao : null;
  // Config aprovada mas incompleta (ex.: só `procurador`, sem `titular`) não
  // pode virar "não falta nada": exigência sumindo em silêncio é pior do que
  // exigir a mais. Nesse caso vale o padrão da TI para o vínculo que faltou.
  const lista = cfg?.[chave] ?? cfg?.['titular']
    ?? DOCUMENTOS_OBRIGATORIOS_PADRAO[chave] ?? DOCUMENTOS_OBRIGATORIOS_PADRAO['titular'] ?? [];
  return lista.map(t => String(t ?? '').trim()).filter(Boolean);
}

/**
 * Modo de exigência dos documentos. Erro de leitura vira 'aviso' — no escuro,
 * não travar o pedido de um cliente é melhor do que travar.
 */
export async function lerModoDocumentos(): Promise<ModoDocumentos> {
  try {
    const { supabase } = await import('./supabase');
    const { data } = await supabase.from('system_settings')
      .select('value').eq('key', 'cancelamento_documentos_modo').maybeSingle();
    return String((data as any)?.value ?? '').trim() === 'bloqueio' ? 'bloqueio' : 'aviso';
  } catch {
    return 'aviso';
  }
}

export interface ConferenciaDocumentos {
  faltando: string[];
  /** Impede validar? Só quando a lista foi aprovada e o modo virou bloqueio. */
  bloqueia: boolean;
  titulo: string;
  texto: string;
}

/**
 * Item de conferência do "Validar cancelamento". Mostra o que falta e diz, sem
 * rodeio, quando a lista ainda é provisória — o atendente precisa saber que
 * pode cobrar um documento que a diretoria ainda não confirmou.
 */
export function conferirDocumentos(
  documentos: DocumentoDoPedido[] | null | undefined,
  vinculo: string | null | undefined,
  modo: ModoDocumentos = 'aviso',
  configuracao?: Record<string, string[]> | null,
): ConferenciaDocumentos {
  const obrigatorios = documentosObrigatoriosDoVinculo(vinculo, configuracao);
  const enviados = documentos || [];

  // Sem nenhum tipo marcado nos anexos (é o caso hoje: o cliente só sobe o
  // arquivo), o que dá para conferir é se veio alguma coisa.
  const temTipoMarcado = enviados.some(d => String(d.tipo_documento ?? '').trim());
  const faltando = temTipoMarcado
    ? documentosQueFaltam(enviados, obrigatorios)
    : (enviados.length === 0 ? obrigatorios : []);

  if (faltando.length === 0) {
    return {
      faltando: [],
      bloqueia: false,
      titulo: 'Documentos',
      texto: enviados.length === 1
        ? '1 documento anexado ao pedido.'
        : `${enviados.length} documentos anexados ao pedido.`,
    };
  }

  const usandoPadrao = !configuracao || Object.keys(configuracao).length === 0;
  return {
    faltando,
    bloqueia: modo === 'bloqueio',
    titulo: 'Documentos do solicitante',
    texto: `Falta anexar: ${faltando.join(', ')}.`
      + (usandoPadrao ? ' Lista provisória da TI — a definitiva depende da decisão da diretoria.' : ''),
  };
}
